import {
  BasicIcon,
  BasicIconsType,
  Collapsible,
} from "@pickit/pickit-components";
import classNames from "classnames";
import DOMPurify from "dompurify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QuestionType, TABS_ICONS } from "../../types";
import { useTranslation } from "react-i18next";
import "../../faq.scss";
import "./faqTab.scss";
import { FaqTabType } from "./type";
import { CountryCodeType, FAQS_PROPS, OPERATE_SUB_TABS, routes } from "utils/constants";
import { useLocation } from "react-router-dom";
import { pushSelectFaq } from "utils/dataLayer";

const FaqTab = ({
  initialQuestion,
  tab,
  backPage,
  back,
  setFirstRender,
}: FaqTabType) => {
  const { t, ready, i18n } = useTranslation();
  const [currentFaq, setCurrentFaq] = useState(
    typeof initialQuestion === "number" ? initialQuestion : -1
  );
  const activeQuestionRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();
  const [, country] = pathname?.split("/") as [
    undefined,
    CountryCodeType,
    string
  ];
  const i18nLanguage = i18n.language || "es_AR";
  const countryFromLanguage = i18nLanguage.split("_")[1]?.toUpperCase() || "AR";
  const countryUpperCase = (country?.toUpperCase() || countryFromLanguage) as CountryCodeType;

  const questions: QuestionType[] = useMemo(
    () =>
      t(`${tab}:questions`, {
        ...FAQS_PROPS[countryUpperCase],
        returnObjects: true,
      }),
    [t, tab, countryUpperCase]
  );
  const scroll = useCallback(() => {
    if (
      tab !== "faq" &&
      activeQuestionRef.current &&
      window.innerHeight -
        activeQuestionRef.current.getBoundingClientRect().bottom <
        0
    ) {
      window.scroll({
        top: activeQuestionRef.current.offsetTop - 60,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [tab]);

  useEffect(() => {
    setTimeout(() => {
      scroll();
    }, 250);
  }, [scroll]);

  const onQuestionClicked = useCallback(
    (index: number) => {
      currentFaq !== index && pushSelectFaq(t(`${tab}:title`), questions[index].question);
      setCurrentFaq((prev) => (prev === index ? -1 : index));
      setTimeout(() => {
        scroll();
      }, 350);
    },
    [currentFaq, questions, scroll, t, tab]
  );

  const renderAnswer = useCallback((question: QuestionType): string => {
    let { answer } = question;

    if (question.answers?.length > 0) {
      let answers = ``;
      answers += `<ul class='faq-answer-list'>`;
      question.answers.forEach((a) => {
        answers += `<li>${a}</li>`;
      });
      answers += `</ul>`;
      answer = answer.replace(`*list*`, answers);
    }

    if (question.extraAnswers?.length > 0) {
      let extraAnswers = ``;
      extraAnswers += `<ul class='faq-answer-list'>`;
      question.extraAnswers.forEach((a) => {
        extraAnswers += `<li>${a}</li>`;
      });
      extraAnswers += `</ul>`;
      answer = answer.replace(`*extralist*`, extraAnswers);
    }

    if (question.extraExtraAnswers?.length > 0) {
      let extraExtraAnswers = ``;
      extraExtraAnswers += `<ul class='faq-answer-list'>`;
      question.extraExtraAnswers.forEach((a) => {
        extraExtraAnswers += `<li>${a}</li>`;
      });
      extraExtraAnswers += `</ul>`;
      answer = answer.replace(`*extraextralist*`, extraExtraAnswers);
    }

    if (question.numberedAnswers?.length > 0) {
      let numberdAnswers = ``;
      numberdAnswers += `<ul class='faq-number-answer-list'>`;
      question.numberedAnswers.forEach((a) => {
        numberdAnswers += `<li>${a}</li>`;
      });
      numberdAnswers += `</ul>`;
      answer = answer.replace(`*123*`, numberdAnswers);
    }

    if (question.links?.length > 0) {
      for (let link of question.links) {
        let href = link.href;
        let target = "_blank";
        if (
          link.name === "@@terms@@" ||
          link.name === "@@conditions@@" ||
          link.label.toLowerCase().includes("términos y condiciones") ||
          link.label.toLowerCase().includes("términos")
        ) {
          const countryForLink = countryUpperCase || "AR";
          href = routes.TERMS.replace(":countryCode", countryForLink);
          target = "_self";
        }
        answer = answer.replace(
          link.name,
          `<a class="faq-answer-link" target="${target}" href="${href}">${link.label}</a>`
        );
      }
    }

    return answer;
  }, [countryUpperCase]);

  const renderQuestions = useCallback(() => {
    if (!ready || !Array.isArray(questions)) return null;
    return questions?.map((question, index) => {
      return (
        <div
          key={index}
          className={classNames("question-container", {
            active: currentFaq === index,
          })}
          ref={currentFaq === index ? activeQuestionRef : undefined}
        >
          <Collapsible
            externalControl
            onClick={() => onQuestionClicked(index)}
            open={currentFaq === index}
            startsOpen={currentFaq === index}
          >
            <div className="faq-label">{question.question}</div>
            <div
              className="faq-label faq-answer"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(renderAnswer(question), {
                  USE_PROFILES: { html: true },
                  ADD_ATTR: ["target"],
                }),
              }}
            />
          </Collapsible>
        </div>
      );
    });
  }, [questions, ready, currentFaq, onQuestionClicked, renderAnswer]);

  const handleClick = useCallback(
    (page: string) => () => {
      if (back) back(page);
      if (setFirstRender) setFirstRender();
    },
    [back, setFirstRender]
  );

  return (
    <>
      {back &&
        backPage &&
        (OPERATE_SUB_TABS.includes(tab) ? (
          <button className="operate-option active">
            <p>{t(`${tab}:title`)}</p>
            <BasicIcon name="x" clickable onClick={handleClick(backPage)} />
          </button>
        ) : (
          <button className="mobile-option">
            <div>
              <BasicIcon
                name={TABS_ICONS[tab] as BasicIconsType}
                color="#0098ff"
              />
              <p>{t(`${tab}:title`)}</p>
            </div>
            <BasicIcon name="x" clickable onClick={handleClick(backPage)} />
          </button>
        ))}
      {renderQuestions()}
    </>
  );
};
export default FaqTab;
